"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getAccrualCalculator = exports.getDaysInYear = exports.getDaysInMonth = exports.calculateProjectedInterest = void 0;
const common_1 = require("@finley-technologies/common");
const finley_types_1 = require("@finley-technologies/finley-types");
/**
 * *** SHOULD ONLY USE FOR DEV TESTING ***
 * Just a simple override flag to disable accruing interest, as it can
 * be easier to test out different loan scenarios (draws, payments, etc.)
 * without having daily accruals muddying up the accounting entries
 */
const DEV_SKIP_ACCRUALS = false;
const setupLogInfo = (logFunc) => {
    const func = (message, level) => {
        if (logFunc)
            logFunc({ message }, level ?? finley_types_1.Log.LogLevel.Info);
    };
    return func;
};
const calculateProjectedInterest = ({ balance, dayCountConvention, targetFundingDate, maturityDate, allInRate, }) => {
    let dayCount = 360;
    if (dayCountConvention === 'Actual / 365') {
        dayCount = 365;
    }
    const days = Math.abs(common_1.date.diffDays(targetFundingDate, maturityDate));
    const projectedInterest = balance * (allInRate / dayCount) * days;
    return projectedInterest;
};
exports.calculateProjectedInterest = calculateProjectedInterest;
const getDaysInMonth = (year, month) => new Date(year, month, 0).getDate();
exports.getDaysInMonth = getDaysInMonth;
// Can be tested later, no one is using Actual / Actual yet
const getDaysInYear = (year) => (year % 4 === 0 && year % 100 > 0) || year % 400 === 0 ? 366 : 365;
exports.getDaysInYear = getDaysInYear;
// Returns a weighted percentage corresponding to the day in question
// Example: 30/360
const getDayBasis = (date, basis) => {
    const [year, _month, _day] = date.split('-');
    // const daysInMonth = getDaysInMonth(Number(year), Number(month));
    const daysInYear = (0, exports.getDaysInYear)(Number(year));
    switch (basis) {
        case '30 / 360':
            // TODO: revisit
            return 30 / 360;
        case '30 / 365':
            // TODO: revisit
            return 30 / 365;
        case 'Actual / 360':
            return 1 / 360;
        case 'Actual / 365':
            return 1 / 365;
        case 'Actual / Actual':
            return 1 / daysInYear;
        default:
            return (0, common_1.finleyAssertUnreachable)(basis);
    }
};
/**
 * Per alignment with product, there's low likelihood where we need to accrue interest
 * that far into the past (e.g., when we're backdating a request). Thus, to ease the
 * data and computation need, we're limiting the start date to 3 months ago.
 * If we require data earlier than this period, we can look to manage these one-off
 * cases independently as needed.
 */
const getAdjustedAccrualPeriodDates = (params) => {
    const { startDate, endDate, logFunc } = params;
    const logInfo = setupLogInfo(logFunc);
    const today = common_1.date.currentDateStr();
    const baseStartComparisonDate = endDate < today ? endDate : today;
    const threeMonthsPriorToBase = common_1.date.addMonths(baseStartComparisonDate, -3);
    if (startDate < threeMonthsPriorToBase) {
        logInfo(`Interest Accrual: [startDate:${startDate}] is more than 3 months prior to base date [today:${today}] [endDate:${endDate}]. Adjusting start date to ${threeMonthsPriorToBase}`);
        return { startDate: threeMonthsPriorToBase, endDate };
    }
    return { startDate, endDate };
};
// With current expectations, each loan maintains a static balance at funding through maturity
// Accrual calculation does not require transaction history, just loan balances
const getAccrualCalculator = ({ loan, }) => {
    const fees = [];
    const interestPeriods = [];
    const interestRates = loan.pricing.history.map((entry) => ({
        effectiveDate: entry.effectiveDate,
        determinationDate: entry.baseRate.determinationDate,
        value: entry.baseRate.adjustedRate,
    }));
    const fetchBaseRate = (currentDate) => {
        const arrayIndex = interestRates.findIndex(({ effectiveDate }) => effectiveDate > currentDate);
        if (arrayIndex < 0) {
            // Use last known rate
            return interestRates[interestRates.length - 1]?.value ?? 0;
        }
        // Use element right before which matches expected effective period
        return interestRates[arrayIndex - 1]?.value ?? 0;
    };
    // Interest periods
    // Skip this for now because invoice handling makes payment date management redundant
    // > expand interest.period config using end dates and period shift if defined
    return {
        fees,
        interestPeriods,
        interestRates,
        calculateInterest: ({ longTermDebtBalances, period, logFunc }) => {
            const logInfo = setupLogInfo(logFunc);
            if (common_1.utils.EnvUtils.isDevelopment() && DEV_SKIP_ACCRUALS) {
                return [];
            }
            const accruals = [];
            const { targetFundingDate } = loan.dates;
            const { startDate, endDate } = getAdjustedAccrualPeriodDates({
                startDate: period.startDate ?? targetFundingDate,
                endDate: period.endDate,
                logFunc,
            });
            // Noting that accruing interest past maturity date is technically allowed (per product),
            // as long as the loan still has principal balance outstanding, thus no assertion here
            (0, common_1.finleyAssert)(startDate <= endDate, `Accrual period start date (${startDate}) must be on or before end date ${endDate}`);
            (0, common_1.finleyAssert)(startDate >= targetFundingDate, `Accrual period start date (${startDate}) must be on or after loan funding date ${targetFundingDate}`);
            logInfo(`Interest Accrual: attempting to accrue interest for [startDate:${startDate}] to [endDate:${endDate}] for [loadId:${loan.id}]`);
            for (const lenderShareItem of longTermDebtBalances) {
                const doesShareExistOnLoan = loan.funding.lenderShares.some((ls) => ls.lenderShareId === lenderShareItem.lenderShareId);
                (0, common_1.finleyAssert)(doesShareExistOnLoan, `Received lender share from entry balances that is not part of loan [inputLenderShareId:${lenderShareItem.lenderShareId}] [loanId:${loan.id}]`);
                if (lenderShareItem.balanceDateMap.size === 0) {
                    logInfo(`Interest Accrual: had 0 balance activity so no-op for [lenderShareId:${lenderShareItem.lenderShareId}] [loadId:${loan.id}]`);
                    continue;
                }
                const marginRate = loan.pricing.margins.find(({ organizationId }) => organizationId === lenderShareItem.capitalProviderId)?.rate ?? 0;
                let runningBalance = 0;
                let currentDate = targetFundingDate; // always start from the target funding date when keeping running balance of Long Term Debt to make sure we have an accurate running balance to calculate interest on
                // eslint-disable-next-line no-restricted-syntax
                while (currentDate < endDate) {
                    const fetchedBalance = lenderShareItem.balanceDateMap.get(currentDate);
                    runningBalance += fetchedBalance ?? 0;
                    // skip accrual activity if the current date is before the start date
                    // we only want to accrue interest on the balance from the start date to the end date
                    // as passed in by the period input param
                    if (currentDate < startDate) {
                        currentDate = common_1.date.addDays(currentDate, 1);
                        continue;
                    }
                    // No need for accrual activity if there's 0 balance (i.e., no interest to accrue)
                    if (runningBalance === 0) {
                        logInfo(`Interest Accrual: had zero running balance while attempting to accrue interest. Will continue to the next day. [lenderShareId:${lenderShareItem.lenderShareId}] [loadId:${loan.id}] [currentDate:${currentDate}] [runningBalance:${runningBalance}] [balanceForDay:${fetchedBalance}]`, finley_types_1.Log.LogLevel.Debug);
                        currentDate = common_1.date.addDays(currentDate, 1);
                        continue;
                    }
                    // Negative balance should not be allowed but currently letting this pass for now
                    if (runningBalance < 0) {
                        logInfo(`Interest Accrual: had negative running balance while attempting to accrue interest. Will continue to the next day. [lenderShareId:${lenderShareItem.lenderShareId}] [loadId:${loan.id}] [currentDate:${currentDate}] [runningBalance:${runningBalance}] [balanceForDay:${fetchedBalance}]`, finley_types_1.Log.LogLevel.Warn);
                        currentDate = common_1.date.addDays(currentDate, 1);
                        continue;
                    }
                    const baseRate = fetchBaseRate(currentDate);
                    const allInRate = baseRate + marginRate;
                    const dayBasis = getDayBasis(currentDate, loan.pricing.accrual.basis);
                    const numberOfDays = 1; // Fixed daily accrual - as the calculator evolves we'll support more options
                    const value = runningBalance * allInRate * dayBasis * numberOfDays;
                    accruals.push({
                        tAccount: finley_types_1.Accounting.CurrentLiabilityAccounts.AccruedInterest,
                        creditFacilityId: loan.facility.creditFacilityId,
                        organizationId: lenderShareItem.capitalProviderId,
                        lenderShareId: lenderShareItem.lenderShareId,
                        date: currentDate,
                        value,
                        proforma: false,
                        variables: [
                            { name: 'principalBalance', value: runningBalance },
                            { name: 'baseRate', value: baseRate },
                            { name: 'marginRate', value: marginRate },
                            { name: 'allInRate', value: allInRate },
                            { name: 'dayBasis', value: dayBasis },
                            { name: 'numberOfDays', value: numberOfDays },
                        ], // TODO: @sachinr4 to save to database for additional visibility with accrual management
                    });
                    // Reconcile against accrual value for overall loan
                    // Possible rounding variances may exist between loan level accrual and sum of share accruals
                    currentDate = common_1.date.addDays(currentDate, 1);
                }
            }
            logInfo(`Interest Accrual: generated ${accruals.length} accrual entries for [loadId:${loan.id}]`);
            return accruals;
        },
        calculateInterestForAdminAgentPrincipal: ({ adminAgentLenderShare, adminAgentPrincipalBalances, period, logFunc, }) => {
            const logInfo = setupLogInfo(logFunc);
            if (common_1.utils.EnvUtils.isDevelopment() && DEV_SKIP_ACCRUALS) {
                return [];
            }
            const { targetFundingDate } = loan.dates;
            const { startDate, endDate } = getAdjustedAccrualPeriodDates({
                startDate: period.startDate ?? targetFundingDate,
                endDate: period.endDate,
                logFunc,
            });
            // Noting that accruing interest past maturity date is technically allowed (per product),
            // as long as the loan still has principal balance outstanding, thus no assertion here
            (0, common_1.finleyAssert)(startDate <= endDate, `Accrual period start date (${startDate}) must be on or before end date ${endDate}`);
            (0, common_1.finleyAssert)(startDate >= targetFundingDate, `Accrual period start date (${startDate}) must be on or after loan funding date ${targetFundingDate}`);
            logInfo(`Admin Agent Interest Accrual: attempting to accrue interest for [startDate:${startDate}] to [endDate:${endDate}] for [loadId:${loan.id}]`);
            const accruals = [];
            for (const lenderShareItem of adminAgentPrincipalBalances) {
                if (lenderShareItem.capitalProviderId ===
                    adminAgentLenderShare.capitalProviderId) {
                    logInfo(`Admin Agent Interest Accrual: came across Admin Agent's own lender share when attempting to accrue interest on fronted balance so no-op for [lenderShareId:${lenderShareItem.lenderShareId}] [loadId:${loan.id}]`, finley_types_1.Log.LogLevel.Warn);
                    continue;
                }
                if (lenderShareItem.balanceDateMap.size === 0) {
                    logInfo(`Admin Agent Interest Accrual: had 0 balance activity so no-op for [lenderShareId:${lenderShareItem.lenderShareId}] [loadId:${loan.id}]`);
                    continue;
                }
                const marginRate = loan.pricing.margins.find(({ organizationId }) => organizationId === lenderShareItem.capitalProviderId)?.rate ?? 0;
                let runningBalance = 0;
                let currentDate = targetFundingDate; // always start from the target funding date when keeping running balance of Admin Agent Principal to make sure we have an accurate running balance to calculate interest on
                // eslint-disable-next-line no-restricted-syntax
                while (currentDate < endDate) {
                    const fetchedBalance = lenderShareItem.balanceDateMap.get(currentDate);
                    runningBalance += fetchedBalance ?? 0;
                    // skip accrual activity if the current date is before the start date
                    // we only want to accrue interest on the balance from the start date to the end date
                    // as passed in by the period input param
                    if (currentDate < startDate) {
                        currentDate = common_1.date.addDays(currentDate, 1);
                        continue;
                    }
                    // No need for accrual activity if there's 0 balance (i.e., no interest to accrue)
                    if (runningBalance === 0) {
                        logInfo(`Admin Agent Principal Interest Accrual: had zero running balance while attempting to accrue interest. Will continue to the next day. [lenderShareId:${lenderShareItem.lenderShareId}] [loadId:${loan.id}] [currentDate:${currentDate}] [runningBalance:${runningBalance}] [balanceForDay:${fetchedBalance}]`, finley_types_1.Log.LogLevel.Debug);
                        currentDate = common_1.date.addDays(currentDate, 1);
                        continue;
                    }
                    // Negative balance should not be allowed but currently letting this pass for now
                    if (runningBalance < 0) {
                        logInfo(`Admin Agent Principal Interest Accrual: had negative running balance while attempting to accrue interest. Will continue to the next day. [lenderShareId:${lenderShareItem.lenderShareId}] [loadId:${loan.id}] [currentDate:${currentDate}] [runningBalance:${runningBalance}] [balanceForDay:${fetchedBalance}]`, finley_types_1.Log.LogLevel.Warn);
                        currentDate = common_1.date.addDays(currentDate, 1);
                        continue;
                    }
                    const baseRate = fetchBaseRate(currentDate);
                    const allInRate = baseRate + marginRate;
                    const dayBasis = getDayBasis(currentDate, loan.pricing.accrual.basis);
                    const numberOfDays = 1; // Fixed daily accrual - as the calculator evolves we'll support more options
                    const interest = runningBalance * allInRate * dayBasis * numberOfDays;
                    accruals.push({
                        tAccount: finley_types_1.Accounting.CurrentLiabilityAccounts.AccruedInterest,
                        creditFacilityId: loan.facility.creditFacilityId,
                        // Important to note that the "Admin Agent" should be the one
                        // receiving all of these interest accruals rather than the lender
                        // that owes the fronted money
                        organizationId: adminAgentLenderShare.capitalProviderId,
                        lenderShareId: adminAgentLenderShare.id,
                        date: currentDate,
                        value: interest,
                        proforma: false,
                        variables: [
                            { name: 'adminAgentPrincipalBalance', value: runningBalance },
                            { name: 'baseRate', value: baseRate },
                            { name: 'marginRate', value: marginRate },
                            { name: 'allInRate', value: allInRate },
                            { name: 'dayBasis', value: dayBasis },
                            { name: 'numberOfDays', value: numberOfDays },
                        ], // TODO: @sachinr4 to save to database for additional visibility with accrual management
                    });
                    // Reconcile against accrual value for overall loan
                    // Possible rounding variances may exist between loan level accrual and sum of share accruals
                    currentDate = common_1.date.addDays(currentDate, 1);
                }
            }
            logInfo(`Admin Agent Interest Accrual: generated ${accruals.length} accrual entries for [loadId:${loan.id}]`);
            return accruals;
        },
        calculateServicerFee: ({ principalBalances, period, logFunc }) => {
            const logInfo = setupLogInfo(logFunc);
            // TODO @dani @sachin - update to servicer fee account once we start tracking it
            const accruals = [];
            const { originationDate } = loan.dates;
            const { startDate, endDate } = getAdjustedAccrualPeriodDates({
                startDate: period.startDate ?? originationDate,
                endDate: period.endDate,
                logFunc,
            });
            // Servicer fee can still apply after the loan maturity date
            // as long as the loan still has principal balance outstanding, thus no assertion here
            (0, common_1.finleyAssert)(startDate <= endDate, `Accrual period start date (${startDate}) must be on or before end date ${endDate}`);
            (0, common_1.finleyAssert)(startDate >= originationDate, `Accrual period start date (${startDate}) must be on or after loan origination date ${originationDate}`);
            logInfo(`Servicer Fee Calculation: attempting to calculate servicer fee for [startDate:${startDate}] to [endDate:${endDate}] for [loadId:${loan.id}]`);
            for (const lenderShareItem of principalBalances) {
                const doesShareExistOnLoan = loan.funding.lenderShares.some((ls) => ls.lenderShareId === lenderShareItem.lenderShareId);
                (0, common_1.finleyAssert)(doesShareExistOnLoan, `Received lender share from entry balances that is not part of loan [inputLenderShareId:${lenderShareItem.lenderShareId}] [loanId:${loan.id}]`);
                if (lenderShareItem.balanceDateMap.size === 0) {
                    logInfo(`Interest Accrual: had 0 balance activity so no-op for [lenderShareId:${lenderShareItem.lenderShareId}] [loadId:${loan.id}]`);
                    continue;
                }
                const servicerFeeRate = loan.pricing.margins.find(({ organizationId }) => organizationId === lenderShareItem.capitalProviderId)?.skimFee?.rate ?? 0;
                let runningBalance = 0;
                let currentDate = originationDate; // start keeping the running balance from the origination date
                // eslint-disable-next-line no-restricted-syntax
                while (currentDate < endDate) {
                    const fetchedBalance = lenderShareItem.balanceDateMap.get(currentDate);
                    runningBalance += fetchedBalance ?? 0;
                    // No need for accrual activity if there's 0 balance (i.e., no interest to accrue)
                    if (runningBalance === 0) {
                        currentDate = common_1.date.addDays(currentDate, 1);
                        continue;
                    }
                    // skip accrual activity if the current date is before the start date
                    // we only want to accrue interest on the balance from the start date to the end date
                    if (currentDate < startDate) {
                        currentDate = common_1.date.addDays(currentDate, 1);
                        continue;
                    }
                    const dayBasis = getDayBasis(currentDate, loan.pricing.accrual.basis);
                    const numberOfDays = 1; // Fixed daily accrual - as the calculator evolves we'll support more options
                    const value = runningBalance * servicerFeeRate * dayBasis * numberOfDays;
                    accruals.push({
                        tAccount: finley_types_1.Accounting.CurrentLiabilityAccounts.AccruedInterest,
                        creditFacilityId: loan.facility.creditFacilityId,
                        organizationId: lenderShareItem.capitalProviderId,
                        lenderShareId: lenderShareItem.lenderShareId,
                        date: currentDate,
                        value,
                        proforma: false,
                        variables: [
                            { name: 'principalBalance', value: runningBalance },
                            { name: 'servicerFeeRate', value: servicerFeeRate },
                            { name: 'dayBasis', value: dayBasis },
                            { name: 'numberOfDays', value: numberOfDays },
                        ],
                    });
                    // Reconcile against accrual value for overall loan
                    // Possible rounding variances may exist between loan level accrual and sum of share accruals
                    currentDate = common_1.date.addDays(currentDate, 1);
                }
            }
            logInfo(`Servicer Fee Calculation: generated ${accruals.length} accrual entries for [loadId:${loan.id}]`);
            return accruals;
        },
    };
};
exports.getAccrualCalculator = getAccrualCalculator;
//# sourceMappingURL=accrual.js.map